import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { Box, IconButton, Tooltip } from "@mui/material";
import {
  OpenInFull,
  CloseFullscreen,
  KeyboardDoubleArrowRight,
} from "@mui/icons-material";
import CustomModal from "../Modal/CustomModal";
import CustomButton from "../Buttons/Button/CustomButton";

/**
 * PreviewDrawer 内の編集フォームが未保存(dirty)状態を最寄りの PreviewDrawer に伝えるための Context。
 * 詳細ページ側は `usePreviewDrawerUnsaved(dirty)` を 1 行呼ぶだけでよい。
 * 確認モーダル・閉じ経路の制御は PreviewDrawer 本体が共通で担う。
 */
const PreviewDrawerGuardContext = createContext<{
  setDirty: (dirty: boolean) => void;
} | null>(null);

/**
 * PreviewDrawer 内の編集フォームから未保存状態を報告するフック。
 * PreviewDrawer の外 (単独ページ等) では no-op。
 */
export const usePreviewDrawerUnsaved = (dirty: boolean): void => {
  const ctx = useContext(PreviewDrawerGuardContext);
  useEffect(() => {
    ctx?.setDirty(dirty);
  }, [ctx, dirty]);
  // アンマウント時 (ドロワーが閉じて children が消える時) に dirty を解除する。
  useEffect(() => {
    return () => ctx?.setDirty(false);
  }, [ctx]);
};

const MIN_WIDTH_PX = 560;
const DEFAULT_WIDTH_RATIO = 0.5;

const resolveDrawerWidth = (width?: string | number): number => {
  if (typeof window === "undefined") return 600;

  let resolvedWidth = Math.floor(window.innerWidth * DEFAULT_WIDTH_RATIO);
  if (typeof width === "number") {
    resolvedWidth = width;
  } else if (width?.endsWith("%")) {
    resolvedWidth = Math.floor(window.innerWidth * (parseFloat(width) / 100));
  }

  return Math.max(
    MIN_WIDTH_PX,
    Math.min(window.innerWidth - 40, resolvedWidth),
  );
};

export interface PreviewDrawerProps {
  open: boolean;
  onClose: () => void;
  /** 全画面化 (省略時は全画面ボタン非表示) */
  onExpand?: () => void;
  children?: React.ReactNode;
  width?: string | number;
}

/**
 * Notion 風プレビュードロワー
 *
 * - 背景操作可能 (バックドロップなし)
 * - 左端ドラッグでリサイズ可能
 * - 全画面ボタン (onExpand 指定時のみ)
 * - ESC で閉じる
 */
const PreviewDrawer: React.FC<PreviewDrawerProps> = ({
  open,
  onClose,
  onExpand,
  children,
  width,
}) => {
  const [fullScreen, setFullScreen] = useState(false);
  const [drawerWidth, setDrawerWidth] = useState<number>(() =>
    resolveDrawerWidth(width),
  );
  const draggingRef = useRef(false);

  // 未保存ガード: 中の編集フォームが usePreviewDrawerUnsaved で報告した dirty を保持し、
  // 閉じ操作時に破棄確認を挟む。確認UI・閉じ制御は PreviewDrawer 側に集約する。
  const [embeddedDirty, setEmbeddedDirty] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const guardCtx = useMemo(() => ({ setDirty: setEmbeddedDirty }), []);
  const requestClose = () => {
    if (embeddedDirty) setShowDiscardConfirm(true);
    else onClose();
  };
  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  useEffect(() => {
    if (!open) {
      setFullScreen(false);
      setShowDiscardConfirm(false);
      setEmbeddedDirty(false);
      return;
    }

    // ドロワーは close 時にもアンマウントされないため、前回のドラッグ幅が state に残る。
    // 開くたびに既定幅（width 指定があればその幅）へ戻す。
    setDrawerWidth(resolveDrawerWidth(width));
  }, [open, width]);

  useEffect(() => {
    const onResize = () => {
      setDrawerWidth((w) => Math.min(w, window.innerWidth - 40));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (fullScreen) return;
    e.preventDefault();
    draggingRef.current = true;
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
    const onMouseMove = (ev: MouseEvent) => {
      if (!draggingRef.current) return;
      const newWidth = window.innerWidth - ev.clientX;
      setDrawerWidth(
        Math.max(MIN_WIDTH_PX, Math.min(window.innerWidth - 40, newWidth)),
      );
    };
    const onMouseUp = () => {
      draggingRef.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  useEffect(() => {
    // 破棄確認モーダル表示中は Esc を拾わない (確認を Esc で閉じられるように)。
    if (!open || showDiscardConfirm) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (embeddedDirty) setShowDiscardConfirm(true);
      else onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose, embeddedDirty, showDiscardConfirm]);

  if (typeof window === "undefined") return null;

  const node = (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        height: "100vh",
        width: fullScreen ? "100vw" : { xs: "100vw", md: `${drawerWidth}px` },
        backgroundColor: "#fff",
        zIndex: 1200,
        borderLeft: "1px solid #E5E5E5",
        transform: open ? "translateX(0)" : "translateX(100%)",
        transition: draggingRef.current
          ? "transform 300ms ease"
          : "transform 300ms ease, width 200ms ease",
        display: "flex",
        flexDirection: "column",
        pointerEvents: open ? "auto" : "none",
      }}
    >
      {!fullScreen && (
        <Box
          onMouseDown={handleMouseDown}
          sx={{
            position: "absolute",
            left: -3,
            top: 0,
            height: "100%",
            width: 6,
            cursor: "ew-resize",
            zIndex: 1,
          }}
        />
      )}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          gap: 0.5,
          px: 1,
          py: 0.25,
        }}
      >
        <Tooltip title="閉じる (ESC)" arrow>
          <IconButton
            size="small"
            onClick={requestClose}
            sx={{ color: "#666", p: 0.5 }}
          >
            <KeyboardDoubleArrowRight fontSize="small" />
          </IconButton>
        </Tooltip>
        {onExpand && (
          <Tooltip title={fullScreen ? "元のサイズに戻す" : "全画面表示"} arrow>
            <IconButton
              size="small"
              onClick={() => {
                if (fullScreen) setFullScreen(false);
                else onExpand();
              }}
              sx={{ color: "#666", p: 0.5 }}
            >
              {fullScreen ? (
                <CloseFullscreen sx={{ fontSize: 16 }} />
              ) : (
                <OpenInFull sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Box
        sx={{
          flex: 1,
          // 縦スクロールのみ。横は出さない (子の負マージン等で左右に動くのを防ぐ)。
          overflowY: "auto",
          overflowX: "hidden",
          backgroundColor: "#fff",
          px: 1.5,
        }}
      >
        <PreviewDrawerGuardContext.Provider value={guardCtx}>
          {open && children}
        </PreviewDrawerGuardContext.Provider>
      </Box>

      {/* 未保存ガード: dirty 状態で閉じようとした時の確認 (× / Esc 共通)。
          文言・デザインはページ遷移ガード (CustomFormNavigationGuard) と揃える。 */}
      <CustomModal
        openModal={showDiscardConfirm}
        handleCloseModal={() => setShowDiscardConfirm(false)}
        title="ページ移動の確認"
        bodyText="入力中の情報が失われますが、よろしいでしょうか？"
        modalType="deleteModal"
        bodyTextAlign="left"
        hideCloseButton
        sx={{ padding: "0px !important" }}
      >
        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => setShowDiscardConfirm(false)}
            sx={{ width: "50% !important" }}
          />
          <CustomButton
            label="移動"
            buttonCategory="confirm"
            onClick={confirmDiscard}
            sx={{ width: "50% !important" }}
          />
        </Box>
      </CustomModal>
    </Box>
  );

  return createPortal(node, document.body);
};

export default PreviewDrawer;
